import React from 'react';

import type { EditorView } from '@atlaskit/editor-prosemirror/view';

import type {
	AgentManagedExtensionAttributes,
	AgentManagedExtensionPlugin,
} from '../common/agentManagedExtensionPluginType';
import { pluginKey } from '../common/agentManagedExtensionPluginType';
import { isExecutingLifecycleState } from '../common/lifecycle';
import {
	findAgentManagedExtension,
	findAgentManagedExtensionByLocalId,
	getNodeAiManagedObject,
} from '../common/utils';

import {
	cacheAiManagedObject,
	closeConfig,
	openConfig,
	toggleConfig,
} from './editor-commands/configure-popup';
import { createPlugin } from './pm-plugins/main';
import { AgentManagedExtensionConfigurePopup } from './ui/AgentManagedExtensionConfigurePopup';
import { executeAiManagedObject } from './ui/executeAiManagedObject';

/**
 * Slim plugin entry-point for the agent-managed extension. Identity,
 * quick-insert, contextual toolbar and body renderer all live on the
 * manifest (see `getAgentManagedExtensionManifest`). This plugin owns
 * only the cross-cutting concerns that don't fit the manifest pattern:
 * schema registration, configure-popup state, the auto-open-on-insert
 * `view`, the Customize-button DOM event bridge, and the editor
 * commands surfaced via `extensionAPI`.
 */
export const agentManagedExtensionPlugin: AgentManagedExtensionPlugin = ({ api, config }) => {
	const { cloudId, contentType } = config ?? {};
	let currentEditorView: EditorView | undefined;
	return {
		name: 'agentManagedExtension',

		pmPlugins() {
			return [
				{
					name: 'agentManagedExtension',
					plugin: ({ nodeViewPortalProviderAPI }) =>
						createPlugin({
							cloudId,
							contentType,
							getContextIdentifierProvider: () =>
								api?.contextIdentifier?.sharedState.currentState()?.contextIdentifierProvider,
							nodeViewPortalProviderAPI,
						}),
				},
			];
		},

		actions: {
			cacheAiManagedObject: (aiManagedObject, localId) => {
				api?.core?.actions.execute(cacheAiManagedObject(aiManagedObject, localId));
			},
			hasAiManagedObject: (localId) => {
				const found = currentEditorView
					? findAgentManagedExtensionByLocalId(currentEditorView.state, localId)
					: undefined;
				const attrs = found?.node.attrs as AgentManagedExtensionAttributes | undefined;
				return Boolean(getNodeAiManagedObject(attrs));
			},
			openConfig: () => {
				api?.core?.actions.execute(openConfig());
			},
			closeConfig: () => {
				api?.core?.actions.execute(closeConfig());
			},
			regenerate: ({ aimoId: fallbackAimoId, localId }) => {
				const editorView = currentEditorView;
				if (!editorView?.editable) {
					return;
				}

				const found =
					findAgentManagedExtensionByLocalId(editorView.state, localId) ??
					findAgentManagedExtension(editorView.state);
				if (!found) {
					return;
				}

				const blockLocalId = (found.node.attrs.localId as string | undefined) || localId;
				const pluginState = pluginKey.getState(editorView.state);
				const executionLifecycleState = blockLocalId
					? pluginState?.executionLifecycleByLocalId[blockLocalId]?.state
					: undefined;
				if (isExecutingLifecycleState(executionLifecycleState)) {
					return;
				}

				const aimoId = getNodeAiManagedObject(found.node.attrs)?.id ?? fallbackAimoId;
				if (!aimoId) {
					return;
				}

				void executeAiManagedObject({ aimoId, editorView, localId: blockLocalId });
			},
			toggleConfig: () => {
				api?.core?.actions.execute(toggleConfig());
			},
		},

		contentComponent({ editorView, popupsMountPoint }) {
			if (!editorView) {
				return null;
			}
			currentEditorView = editorView;
			return (
				<AgentManagedExtensionConfigurePopup
					api={api}
					cloudId={cloudId}
					contentType={contentType}
					editorView={editorView}
					mountPoint={popupsMountPoint}
				/>
			);
		},

		getSharedState(editorState) {
			if (!editorState) {
				return undefined;
			}
			return pluginKey.getState(editorState) ?? undefined;
		},
	};
};
